import { useContext } from 'react';
import * as R from 'ramda';

import { Icon, List } from '@atom/mui';
import { CategoryPathItem } from '@atom/types/inventory';
import attributeDisplayUtility from '@atom/utilities/attributeDisplayUtility';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { hasElements, isAsset } from './InventoryAssetTreeTransfer';
import TreeContext, { Tree } from './TreeContext';
import TreeRowIcon from './TreeRowIcon';

const { ListItemButton, ListItemText } = List;

export interface Props {
  isAncestorSelected?: boolean;
  hasChildren: boolean;
  level: number;
  tree: Tree;
  path: CategoryPathItem[];
  assetPath: CategoryPathItem[];
}

const getListItemStyle = (hasChildren: boolean, level: number) =>
  hasChildren
    ? { paddingLeft: `${level}rem`, paddingRight: '1rem' }
    : { paddingLeft: `${level + 2}rem`, paddingRight: '1rem' };

const getEmptyStyles = (level: number) => {
  return {
    paddingLeft: `${level + 4}rem`,
    paddingRight: '1rem',
  };
};

const styles = {
  toggle: {
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
};

const TreeItem = ({ hasChildren, level, tree, path, assetPath }: Props) => {
  const { handleAssetClick, handleToggle, open, rootTree, filterParams } =
    useContext(TreeContext);

  if (isNilOrEmpty(tree)) {
    return (
      <ListItemButton
        disableGutters
        style={getEmptyStyles(level)}
        disabled
        onClick={() => {}}
      >
        <ListItemText primary="There are 0 matching assets." />
      </ListItemButton>
    );
  }

  if (!rootTree) {
    return null;
  }

  const { elementSchemaId } = filterParams;

  const disabled =
    (!!elementSchemaId &&
      isAsset(tree) &&
      tree.schemaId !== elementSchemaId &&
      !hasChildren) ||
    (tree as any).inactive;

  const hasElementItems =
    isAsset(tree) && hasElements(tree) && tree.schemaId !== elementSchemaId;

  const getAttributeSubText = (element: any): string => {
    if (isNilOrEmpty(element.attributes)) {
      return '';
    }

    const attributeIds = R.pathOr([], ['attributeGroups'], element).reduce(
      (acc, attributeGroup) => {
        return [...acc, ...R.pathOr([], ['attributes'], attributeGroup)];
      },
      [],
    );

    return attributeIds.reduce((acc: string, attributeId: string): any => {
      const attribute = element.attributes[attributeId];
      const label = attribute.name;
      const value = attributeDisplayUtility.display(
        attribute.value,
        attribute.dataType,
        attribute.unit,
      );

      const text = `${label}: ${value}`;
      return acc ? `${acc}, ${text}` : text;
    }, '');
  };

  const subtext = getAttributeSubText(tree);

  return (
    <ListItemButton
      disableGutters
      style={getListItemStyle(hasChildren, level)}
      disabled={disabled}
      onClick={
        isAsset(tree) && !hasElementItems
          ? () => handleAssetClick(tree, assetPath)
          : () => handleToggle(tree, path)
      }
    >
      {hasChildren && (
        <Icon style={styles.toggle}>
          {open.has(tree.id) ? 'arrow_drop_down' : 'arrow_right'}
        </Icon>
      )}
      <TreeRowIcon tree={tree} />
      <ListItemText primary={tree.name} secondary={subtext} />
    </ListItemButton>
  );
};

export default TreeItem;
